import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DocuSignConfigCard } from "@/components/DocuSignEnvelope";
import BankSyncCard from "@/components/BankSyncCard";
import { GhlConfigCard, GhlFieldMapCard } from "@/components/GhlIntegration";
import { GhlSmsTestCard } from "@/components/GhlSmsTestCard";
import { SmsBroadcastCard } from "@/components/SmsBroadcastCard";
import { DelinquencyNudgesCard } from "@/components/DelinquencyNudgesCard";
import { BillingCard } from "@/components/BillingCard";
import { ImpersonationAuditCard } from "@/components/ImpersonationAuditCard";
import { CommunityCoverCard } from "@/components/CommunityCoverCard";
import { SystemHealthCard } from "@/components/SystemHealthCard";
import { BackupsCard } from "@/components/BackupsCard";
import { BoardMembersCard } from "@/components/BoardMembersCard";
import { BudgetsUploadCard } from "@/components/BudgetsUploadCard";
import { BoardActivityCard } from "@/components/BoardActivityCard";
import { BoardDigestCard } from "@/components/BoardDigestCard";
import { DigestAnalyticsCard } from "@/components/DigestAnalyticsCard";
import PushSettingsCard from "@/components/PushSettingsCard";
import DemoDataPurgeCard from "@/components/DemoDataPurgeCard";

export default function SettingsPage() {
  const { user } = useAuth();
  const { properties, currentProperty } = useProperty();
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoOpenLegalPack, setAutoOpenLegalPack] = useState(false);

  // Deep-link from ⌘K palette: `?legal_pack=1` auto-opens the DocuSign
  // legal-pack dialog. Strip the param after handling so hard-refresh from
  // the same URL doesn't re-open the dialog every time.
  useEffect(() => {
    if (searchParams.get("legal_pack") === "1") {
      setAutoOpenLegalPack(true);
      const next = new URLSearchParams(searchParams);
      next.delete("legal_pack");
      setSearchParams(next, { replace: true });
      // Reset the trigger after DocuSignConfigCard picks it up, so future
      // manual opens work correctly.
      setTimeout(() => setAutoOpenLegalPack(false), 800);
    }
  }, [searchParams, setSearchParams]);

  return (
    <div>
      <PageHeader eyebrow="Admin Tools" title="Settings" subtitle="Account, portfolio, integrations." />
      <div className="mb-4">
        <SystemHealthCard />
      </div>
      <div className="mb-4">
        <PushSettingsCard />
      </div>
      <div className="mb-4">
        <BackupsCard />
      </div>
      <div className="mb-4">
        <DemoDataPurgeCard />
      </div>
      <div className="mb-4">
        <BoardMembersCard />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <BudgetsUploadCard />
        <BoardActivityCard limit={30} />
      </div>
      <div className="mb-4">
        <BoardDigestCard />
      </div>
      <div className="mb-4">
        <DigestAnalyticsCard />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 border-white/10 bg-card">
          <div className="overline mb-3">Owner account</div>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={user?.name || ""} readOnly /></div>
            <div><Label>Email</Label><Input value={user?.email || ""} readOnly /></div>
            <div><Label>Role</Label><Input value={user?.role || ""} readOnly /></div>
            <Button onClick={() => toast.info("Settings save is a demo — extend to persist.")} className="rounded-full">Save</Button>
          </div>
        </Card>
        <Card className="p-5 border-white/10 bg-card">
          <div className="overline mb-3">Portfolio</div>
          <div className="text-sm text-zinc-400 mb-3">Currently managing {properties.length} propert{properties.length === 1 ? "y" : "ies"}.</div>
          <div className="space-y-2">
            {properties.map((p) => (
              <div key={p.id} className={"flex items-center justify-between rounded-md border border-white/10 p-3 " + (currentProperty?.id === p.id ? "bg-orange-500/5" : "")}>
                <div>
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-xs text-zinc-500">{p.type} · {p.units} units</div>
                </div>
                {currentProperty?.id === p.id && <span className="text-xs uppercase tracking-widest text-orange-400">Active</span>}
              </div>
            ))}
          </div>
        </Card>
      </div>
      <div className="mt-4">
        <CommunityCoverCard />
      </div>
      <div className="mt-4">
        <DocuSignConfigCard autoOpenLegalPack={autoOpenLegalPack} />
      </div>
      <div className="mt-4">
        <BankSyncCard />
      </div>
      <div className="mt-4">
        <GhlConfigCard />
      </div>
      <div className="mt-4">
        <GhlSmsTestCard />
      </div>
      <div className="mt-4">
        <SmsBroadcastCard />
      </div>
      <div className="mt-4">
        <GhlFieldMapCard />
      </div>
      <div className="mt-4">
        <DelinquencyNudgesCard />
      </div>
      <div className="mt-4">
        <BillingCard />
      </div>
      <div className="mt-4">
        <ImpersonationAuditCard />
      </div>
    </div>
  );
}
